import { memo, useState, type ReactNode } from 'react';
import { useTerminalStore, selectAllSignals, selectDisplayPrice } from '../../stores/terminal.store.js';
import { WalletConnect } from '../wallet/WalletConnect.js';
import { formatPriceWithSymbol } from '@arc/shared';
import { DEFAULT_LAYOUT, LAYOUT_2, LAYOUT_4, LAYOUT_6 } from './DynamicGrid.js';
import type { Theme } from '@arc/shared';

const THEMES: Array<{ id: Theme; color: string; label: string; accent: string }> = [
  { id: 'arc',     color: '#00BFFF', accent: '#FFD700', label: 'ARC BLUE'   },
  { id: 'gold',    color: '#FFD700', accent: '#FFA500', label: 'GOLD'       },
  { id: 'matrix',  color: '#00FF88', accent: '#AAFF00', label: 'MATRIX'     },
  { id: 'crimson', color: '#DC143C', accent: '#FF6060', label: 'CRIMSON'    },
  { id: 'stark',   color: '#FF6B35', accent: '#FFD700', label: 'STARK'      },
  { id: 'light',   color: '#0066CC', accent: '#FF6600', label: 'LIGHT'      },
];

const UI_SCALES = [
  { value: 0.85, label: 'XS' },
  { value: 1.0,  label: 'S'  },
  { value: 1.1,  label: 'M'  },
  { value: 1.2,  label: 'L'  },
  { value: 1.35, label: 'XL' },
];

export const TopBar = memo(function TopBar() {
  const theme           = useTerminalStore(s => s.theme);
  const wsConnected     = useTerminalStore(s => s.wsConnected);
  const signals         = useTerminalStore(selectAllSignals);
  const setTheme        = useTerminalStore(s => s.setTheme);
  const setPanelLayouts  = useTerminalStore(s => s.setPanelLayouts);
  const clearHiddenPanels = useTerminalStore(s => s.clearHiddenPanels);
  const panelLayouts     = useTerminalStore(s => s.panelLayouts);
  const addPanelSymbol   = useTerminalStore(s => s.addPanelSymbol);
  const ensurePanelCount = useTerminalStore(s => s.ensurePanelCount);
  const activePanels     = useTerminalStore(s => s.activePanels);
  const uiScale         = useTerminalStore(s => s.uiScale);
  const setUiScale      = useTerminalStore(s => s.setUiScale);

  const dbStatus  = useTerminalStore(s => s.dbSyncStatus);
  const dbBackend = useTerminalStore(s => s.dbBackend);
  const [themeOpen, setThemeOpen] = useState(false);

  // Same source + formatter as the chart headers (last candle close → spot fallback)
  const btcPrice = useTerminalStore(selectDisplayPrice('BTC'));
  const ethPrice = useTerminalStore(selectDisplayPrice('ETH'));
  const solPrice = useTerminalStore(selectDisplayPrice('SOL'));

  const buyCount  = signals.filter(s => s.type === 'BUY').length;
  const sellCount = signals.filter(s => s.type === 'SELL').length;

  const currentTheme = THEMES.find(t => t.id === theme) ?? THEMES[0]!;

  // A reset or a preset is a fresh layout: bring back any removed panels too.
  const resetLayout = () => { clearHiddenPanels(); setPanelLayouts([]); };

  const addChart = () => {
    const syms = ['SOL','BNB','WIF','JTO','ETH','BTC'];
    const next   = syms[activePanels.length % syms.length] ?? 'BTC';
    const newId  = `chart-${activePanels.length}`;
    const base   = panelLayouts.length > 0 ? panelLayouts : DEFAULT_LAYOUT;
    const maxY   = Math.max(...base.map(l => l.y + l.h), 0);
    addPanelSymbol(next);
    setPanelLayouts([...base, { i: newId, x: 0, y: maxY, w: 5, h: 6, minW: 2, minH: 3 }]);
  };

  const applyPreset = (items: typeof LAYOUT_2, chartCount: number) => {
    ensurePanelCount(chartCount);
    clearHiddenPanels();
    setPanelLayouts(items);
  };

  // Which preset is currently active (best-effort match by chart count in layout)
  const chartLayoutIds = (panelLayouts.length > 0 ? panelLayouts : DEFAULT_LAYOUT)
    .filter(l => l.i.startsWith('chart-')).map(l => l.i);
  const currentChartCount = chartLayoutIds.length;

  return (
    <header
      className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1 border-b border-hud flex-shrink-0 relative"
      style={{
        minHeight: 46,
        background: `linear-gradient(90deg, var(--color-panel) 0%, color-mix(in srgb, var(--color-panel) 90%, var(--color-arc) 10%) 100%)`,
        boxShadow: '0 1px 0 var(--color-border), 0 2px 12px rgba(0,0,0,0.4)',
        zIndex: 9999,
      }}
    >
      {/* Animated gradient header bottom border */}
      <div
        className="absolute bottom-0 left-0 right-0 pointer-events-none"
        style={{
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, var(--color-arc) 30%, var(--color-accent) 50%, var(--color-arc) 70%, transparent 100%)',
          backgroundSize: '200% 100%',
          animation: 'headerGlowLine 4s ease-in-out infinite',
          zIndex: 10,
        }}
      />

      {/* Animated left-edge accent */}
      <div
        className="absolute left-0 top-0 bottom-0 w-0.5 animate-glow-pulse"
        style={{ background: `linear-gradient(180deg, transparent, var(--color-arc), transparent)` }}
      />

      {/* ── Logo ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Arc reactor — multi-layer gyroscopic design */}
        <div className="relative flex-shrink-0" style={{ width: 36, height: 36 }}>

          {/* Layer 1 — outer segmented ring, slow CCW */}
          <svg width="36" height="36" viewBox="0 0 36 36"
            style={{ position:'absolute', top:0, left:0, animation:'counterSpin 14s linear infinite', filter:'drop-shadow(0 0 6px rgba(0,212,255,0.7))' }}>
            <circle cx="18" cy="18" r="16.5" fill="none" stroke="rgba(0,212,255,0.25)" strokeWidth="0.5" />
            {/* 12 tick marks */}
            {[0,30,60,90,120,150,180,210,240,270,300,330].map(deg => {
              const r = deg * Math.PI / 180;
              const long = deg % 90 === 0;
              return (
                <line key={deg}
                  x1={18 + 14.5 * Math.cos(r)} y1={18 + 14.5 * Math.sin(r)}
                  x2={18 + (long ? 12 : 13.2) * Math.cos(r)} y2={18 + (long ? 12 : 13.2) * Math.sin(r)}
                  stroke="#00d4ff" strokeWidth={long ? 1.2 : 0.6}
                  opacity={long ? 0.9 : 0.45}
                />
              );
            })}
            {/* 4 arc segments with gaps */}
            <circle cx="18" cy="18" r="15" fill="none" stroke="#00d4ff" strokeWidth="1"
              strokeDasharray="16 7.6" opacity="0.5" />
          </svg>

          {/* Layer 2 — orbital ring with 6 glowing nodes, medium CW */}
          <svg width="36" height="36" viewBox="0 0 36 36"
            style={{ position:'absolute', top:0, left:0, animation:'coreSpin 6s linear infinite' }}>
            <circle cx="18" cy="18" r="11" fill="none" stroke="rgba(0,212,255,0.15)" strokeWidth="0.5" strokeDasharray="2 2" />
            {[0,60,120,180,240,300].map(deg => {
              const r = deg * Math.PI / 180;
              const x = 18 + 11 * Math.cos(r);
              const y = 18 + 11 * Math.sin(r);
              const isPrimary = deg % 120 === 0;
              return (
                <circle key={deg} cx={x} cy={y} r={isPrimary ? 1.4 : 0.8}
                  fill="#00d4ff" opacity={isPrimary ? 1 : 0.5} />
              );
            })}
          </svg>

          {/* Layer 3 — hexagon, counter-spin fast */}
          <svg width="36" height="36" viewBox="0 0 36 36"
            style={{ position:'absolute', top:0, left:0, animation:'counterSpin 4s linear infinite' }}>
            <polygon
              points={[0,60,120,180,240,300].map(deg => {
                const r = deg * Math.PI / 180;
                return `${18 + 7.5 * Math.cos(r)},${18 + 7.5 * Math.sin(r)}`;
              }).join(' ')}
              fill="none" stroke="#00d4ff" strokeWidth="0.8" opacity="0.7"
            />
          </svg>

          {/* Layer 4 — tri-blade rotor, fast CW */}
          <svg width="36" height="36" viewBox="0 0 36 36"
            style={{ position:'absolute', top:0, left:0, animation:'coreSpin 2.5s linear infinite' }}>
            {[0, 120, 240].map(deg => {
              const r = deg * Math.PI / 180;
              const r1 = (deg + 30) * Math.PI / 180;
              const ox = 18 + 5 * Math.cos(r);
              const oy = 18 + 5 * Math.sin(r);
              const tx = 18 + 4.5 * Math.cos(r1);
              const ty = 18 + 4.5 * Math.sin(r1);
              return (
                <path key={deg}
                  d={`M 18 18 L ${ox} ${oy} Q ${tx} ${ty} 18 18`}
                  fill="rgba(0,212,255,0.25)" stroke="#00d4ff" strokeWidth="0.5" opacity="0.9"
                />
              );
            })}
          </svg>

          {/* Core — pulsing glow dot, static */}
          <div className="absolute" style={{
            width: 7, height: 7,
            top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            borderRadius: '50%',
            background: '#00d4ff',
            boxShadow: '0 0 8px #00d4ff, 0 0 18px #00d4ff, 0 0 35px rgba(0,212,255,0.8), 0 0 60px rgba(0,212,255,0.3)',
            animation: 'corePulse 2s ease-in-out infinite',
          }} />
        </div>

        {/* Text block */}
        <div>
          <div
            className="font-orb font-black tracking-[4px] leading-none"
            style={{
              fontSize: 15,
              background: 'linear-gradient(90deg, var(--color-arc), var(--color-accent))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            ARC TERMINAL
          </div>
          <div
            className="font-mono tracking-[3px] mt-0.5"
            style={{ fontSize: 11, color: 'var(--color-muted)' }}
          >
            CRYPTO INTELLIGENCE SYSTEM
          </div>
        </div>
      </div>

      <div className="w-px h-5 mx-1" style={{ background: 'var(--color-border)' }} />

      {/* ── Live prices ───────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        {[['BTC', btcPrice], ['ETH', ethPrice], ['SOL', solPrice]].map(([sym, price]) => (
          <div key={sym as string} className="font-mono text-[11px] flex items-center gap-1">
            <span style={{ color: 'var(--color-text)' }}>{sym}</span>
            <span style={{ color: 'var(--color-bright)' }}>
              {(price as number) > 0 ? formatPriceWithSymbol(price as number) : '---'}
            </span>
          </div>
        ))}
      </div>

      <div className="w-px h-5 mx-1" style={{ background: 'var(--color-border)' }} />

      {/* ── Signal counters ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 font-mono text-[11px]">
        <span style={{ color: 'var(--color-text)' }}>SIG</span>
        <span style={{ color: 'var(--color-buy)' }}>{buyCount}▲</span>
        <span style={{ color: 'var(--color-sell)' }}>{sellCount}▼</span>
      </div>

      <div className="flex-1" />

      {/* ── Font size picker ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-1" title="UI Scale">
        <span className="font-mono text-[11px] opacity-90 tracking-widest mr-0.5" style={{ color: 'var(--color-text)' }}>SIZE</span>
        <button
          onClick={() => setUiScale(Math.max(0.85, parseFloat((uiScale - 0.1).toFixed(2))))}
          title="Decrease UI scale"
          aria-label="Decrease UI scale"
          className="font-mono text-[11px] font-bold px-1.5 py-0.5 border transition-all hover:border-arc hover:text-arc"
          style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted)', lineHeight: 1, clipPath: 'polygon(4px 0%, 100% 0%, calc(100% - 4px) 100%, 0% 100%)' }}
        >
          A−
        </button>
        <span
          className="font-mono text-[11px] w-8 text-center border px-1 py-0.5"
          style={{ borderColor: 'var(--color-arc)', color: 'var(--color-arc)', background: 'var(--color-glow)' }}
        >
          {UI_SCALES.find(s => Math.abs(uiScale - s.value) < 0.06)?.label ?? `${Math.round(uiScale * 100)}%`}
        </span>
        <button
          onClick={() => setUiScale(Math.min(1.5, parseFloat((uiScale + 0.1).toFixed(2))))}
          title="Increase UI scale"
          aria-label="Increase UI scale"
          className="font-mono text-[11px] font-bold px-1.5 py-0.5 border transition-all hover:border-arc hover:text-arc"
          style={{ borderColor: 'var(--color-border)', color: 'var(--color-bright)', lineHeight: 1, clipPath: 'polygon(4px 0%, 100% 0%, calc(100% - 4px) 100%, 0% 100%)' }}
        >
          A+
        </button>
      </div>

      <div className="w-px h-5 mx-1" style={{ background: 'var(--color-border)' }} />

      {/* ── Wallet connect ────────────────────────────────────────────────── */}
      <WalletConnect />

      <div className="w-px h-5 mx-1" style={{ background: 'var(--color-border)' }} />

      {/* ── Layout presets (TradingView-style) ───────────────────────────── */}
      <div className="flex items-center gap-0.5" title="Chart layout presets">
        {(([
          { count: 2, preset: LAYOUT_2, icon: (
            <svg width="16" height="12" viewBox="0 0 16 12" fill="currentColor">
              <rect x="0"  y="0" width="7" height="12" rx="1" />
              <rect x="9"  y="0" width="7" height="12" rx="1" />
            </svg>
          )},
          { count: 4, preset: LAYOUT_4, icon: (
            <svg width="16" height="12" viewBox="0 0 16 12" fill="currentColor">
              <rect x="0"  y="0" width="7" height="5"  rx="1" />
              <rect x="9"  y="0" width="7" height="5"  rx="1" />
              <rect x="0"  y="7" width="7" height="5"  rx="1" />
              <rect x="9"  y="7" width="7" height="5"  rx="1" />
            </svg>
          )},
          { count: 6, preset: LAYOUT_6, icon: (
            <svg width="16" height="12" viewBox="0 0 20 12" fill="currentColor">
              <rect x="0"  y="0" width="5" height="5"  rx="1" />
              <rect x="7"  y="0" width="5" height="5"  rx="1" />
              <rect x="14" y="0" width="6" height="5"  rx="1" />
              <rect x="0"  y="7" width="5" height="5"  rx="1" />
              <rect x="7"  y="7" width="5" height="5"  rx="1" />
              <rect x="14" y="7" width="6" height="5"  rx="1" />
            </svg>
          )},
        ]) as Array<{ count: number; preset: typeof LAYOUT_2; icon: ReactNode }>).map(({ count, preset, icon }) => {
          const active = currentChartCount === count;
          return (
            <button
              key={count}
              onClick={() => applyPreset(preset, count)}
              title={`${count}-chart layout`}
              aria-label={`${count}-chart layout`}
              aria-pressed={active}
              className="flex items-center justify-center w-7 h-7 border transition-all"
              style={{
                borderColor:     active ? 'var(--color-arc)' : 'var(--color-border)',
                color:           active ? 'var(--color-arc)' : 'var(--color-muted)',
                background:      active ? 'var(--color-glow)' : 'transparent',
                clipPath: 'polygon(4px 0%, 100% 0%, calc(100% - 4px) 100%, 0% 100%)',
              }}
              onMouseEnter={e => {
                if (!active) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-arc)';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-arc)';
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border)';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-muted)';
                }
              }}
            >
              {icon}
            </button>
          );
        })}
      </div>

      <div className="w-px h-5" style={{ background: 'var(--color-border)' }} />

      {/* ── Add chart ─────────────────────────────────────────────────────── */}
      <button
        onClick={addChart}
        title="Add chart panel"
        aria-label="Add chart panel"
        className="font-mono text-[11px] px-2 py-1 border transition-all tracking-widest hover:scale-105"
        style={{ borderColor: 'var(--color-border)', color: 'var(--color-bright)', clipPath: 'polygon(5px 0%, 100% 0%, calc(100% - 5px) 100%, 0% 100%)' }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-arc)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-arc)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-bright)';
        }}
      >
        + PANEL
      </button>

      {/* ── Reset layout ──────────────────────────────────────────────────── */}
      <button
        onClick={resetLayout}
        title="Reset to default layout"
        aria-label="Reset to default layout"
        className="font-mono text-[11px] px-2 py-1 border transition-all tracking-widest"
        style={{ borderColor: 'var(--color-border)', color: 'var(--color-bright)', clipPath: 'polygon(5px 0%, 100% 0%, calc(100% - 5px) 100%, 0% 100%)' }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-accent)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-accent)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-bright)';
        }}
      >
        ⊞ RESET
      </button>

      <div className="w-px h-5 mx-1" style={{ background: 'var(--color-border)' }} />

      {/* ── Theme picker ──────────────────────────────────────────────────── */}
      <div className="relative">
        <button
          onClick={() => setThemeOpen(p => !p)}
          aria-label={`Theme: ${currentTheme.label}. Change theme`}
          aria-expanded={themeOpen}
          aria-haspopup="listbox"
          title="Change theme"
          className="flex items-center gap-1.5 font-mono text-[11px] px-2 py-1 border transition-all tracking-widest"
          style={{ borderColor: currentTheme.color, color: currentTheme.color }}
        >
          <span
            className="w-2.5 h-2.5 rounded-full inline-block"
            style={{ background: currentTheme.color, boxShadow: `0 0 6px ${currentTheme.color}` }}
          />
          {currentTheme.label}
          <span className="opacity-50">{themeOpen ? '▲' : '▼'}</span>
        </button>

        {themeOpen && (
          <div
            className="absolute right-0 top-full mt-1 border py-1 min-w-[140px] animate-slide-in"
            style={{
              zIndex: 99999,
              background: 'var(--color-panel)',
              borderColor: 'var(--color-border)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            }}
          >
            {THEMES.map(t => (
              <button
                key={t.id}
                onClick={() => { setTheme(t.id); setThemeOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors"
                style={{
                  background: theme === t.id ? `${t.color}18` : 'transparent',
                  color: theme === t.id ? t.color : 'var(--color-text)',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = `${t.color}18`)}
                onMouseLeave={e => (e.currentTarget.style.background = theme === t.id ? `${t.color}18` : 'transparent')}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ background: t.color, boxShadow: theme === t.id ? `0 0 8px ${t.color}` : 'none' }}
                />
                <span className="font-mono text-[11px] tracking-[2px]">{t.label}</span>
                {theme === t.id && <span className="ml-auto text-[11px]" style={{ color: t.color }}>✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-px h-5 mx-1" style={{ background: 'var(--color-border)' }} />

      {/* ── DB status ─────────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-1 font-mono text-[11px] tracking-wider opacity-90"
        title={`Persistence: ${dbBackend}`}
      >
        <span style={{
          color: dbStatus === 'ready'   ? 'var(--color-buy)'
               : dbStatus === 'loading' ? 'var(--color-accent)'
               : dbStatus === 'error'   ? 'var(--color-sell)'
               : 'var(--color-muted)',
        }}>
          {dbStatus === 'ready'   ? '⬡'
         : dbStatus === 'loading' ? '◌'
         : dbStatus === 'error'   ? '⚠'
         : '○'}
        </span>
        <span style={{ color: 'var(--color-muted)' }}>
          {dbBackend === 'supabase' ? 'SUPA' : 'DB'}
        </span>
      </div>

      <div className="w-px h-5 mx-1" style={{ background: 'var(--color-border)' }} />

      {/* ── Connection status ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-1.5" role="status" title={wsConnected ? 'WebSocket connected' : 'WebSocket disconnected — polling REST prices'}>
        <div
          className={`w-1.5 h-1.5 rounded-full animate-blink`}
          style={{ background: wsConnected ? 'var(--color-buy)' : 'var(--color-accent)' }}
        />
        <span className="font-mono text-[11px] tracking-wider" style={{ color: wsConnected ? 'var(--color-buy)' : 'var(--color-accent)' }}>
          {wsConnected ? 'LIVE' : 'POLLING'}
        </span>
      </div>
    </header>
  );
});
